/**
 * Agent configuration (S10).
 *
 * ## Why this is two agents and not seven
 *
 * The platform's `/client/api/v1/agents` list holds every agent in the project.
 * The console shows the two it actually uses: the one that conducts the calls and
 * the one that classifies them afterwards. The rest belong to other work, and an
 * editor that offered them would be inviting an operator to change something with
 * no visible effect here and an unknown effect elsewhere.
 *
 * ## Why the editable fields are a per-agent allowlist
 *
 * `PUT /agents/{id}` takes the whole agent — `configuration`, `name` and
 * `presetName` are all required, and the configuration object carries about
 * thirty keys including tools, MCP servers, HTTP requests, knowledge bases and
 * skills. There is no partial update, so a save is always a full overwrite, and
 * the only thing standing between an edit and a wiped configuration is being
 * explicit about which fields may change. That list differs per agent, so it is
 * declared per role here and enforced again on the server.
 */
import { z } from 'zod';

import { AgentAuditAction } from './enums.js';

/**
 * The two agents the console edits.
 *
 * `OUTBOUND` is the agent on the call. `ANALYZER` reads the transcript afterwards
 * and produces the lead category, sentiment and next action. Roles rather than
 * ids, because the ids are deployment configuration.
 */
export const AGENT_ROLES = ['OUTBOUND', 'ANALYZER'] as const;
export type AgentRole = (typeof AGENT_ROLES)[number];
export const agentRoleSchema = z.enum(AGENT_ROLES);

/** Every field the console can write, across all roles. */
export const AGENT_FIELDS = ['presetName', 'name', 'instructions'] as const;
export type AgentField = (typeof AGENT_FIELDS)[number];
export const agentFieldSchema = z.enum(AGENT_FIELDS);

/**
 * What each role may change. **This is the load-bearing declaration in S10.**
 *
 * The outbound agent introduces itself by `name` on the call, so that field is
 * editable and, since the greeting is built from it, actually audible. The
 * analyzer's name is never spoken or shown to anyone outside the platform, so it
 * is not offered — leaving one less field whose edit would appear to do nothing.
 *
 * Anything absent here is rejected by the server, not merely hidden by the UI.
 */
export const EDITABLE_FIELDS: Record<AgentRole, readonly AgentField[]> = {
  OUTBOUND: ['presetName', 'name', 'instructions'],
  ANALYZER: ['presetName', 'instructions'],
};

/**
 * Platform limits, copied from `AgentV1UpdateRequest`.
 *
 * The instruction cap is ours: the spec sets none, and an unbounded field behind
 * a PUT that must resend the entire configuration is a way to discover a request
 * size limit during a save rather than before one.
 */
export const AGENT_NAME_MAX = 100;
export const AGENT_PRESET_NAME_MAX = 100;
export const AGENT_INSTRUCTIONS_MAX = 100_000;

/** One agent as the list shows it. */
export const agentSummarySchema = z.object({
  id: z.string().uuid(),
  role: agentRoleSchema,
  /** The platform's own id, shown so a row can be matched to the dashboard. */
  sirmaAgentId: z.string(),
  name: z.string(),
  presetName: z.string().nullable(),
  description: z.string().nullable(),
  /** Which fields this agent permits, so the editor renders no dead input. */
  editableFields: z.array(agentFieldSchema),
  /** The platform's `updated`, which is the version token conflicts use. */
  sirmaUpdatedAt: z.string().datetime().nullable(),
  /**
   * Who last changed this agent, as an email.
   *
   * Not simply the platform's `updatedBy`. Sirma records that field as an email
   * when a person edits in its own dashboard and as an opaque API-key id when
   * something edits over the API — which is every change made from here. So a
   * change we made is resolved back to the console user who made it, from the
   * audit row written alongside it.
   *
   * Null when neither is available, which is honest: an id nobody can look up is
   * not an answer to "who", and showing one would only invite the question again.
   */
  lastUpdatedBy: z.string().nullable(),
  lastSyncedAt: z.string().datetime().nullable(),
});
export type AgentSummary = z.infer<typeof agentSummarySchema>;

/**
 * One agent, with the instructions the editor works on.
 *
 * The configuration the console never writes is deliberately *not* here. It was,
 * as counts beside the editor, until the panel showing them was cut — and a field
 * computed on every read for nobody to consume is a claim that somebody does.
 * `preservedCounts` still exists for the sync and merge-verification tools, which
 * is where those numbers are actually read.
 */
export const agentDetailSchema = agentSummarySchema.extend({
  instructions: z.string(),
});
export type AgentDetail = z.infer<typeof agentDetailSchema>;

export const agentListSchema = z.object({ items: z.array(agentSummarySchema) });
export type AgentList = z.infer<typeof agentListSchema>;

/**
 * A save.
 *
 * Every field is optional — an operator who changed only the instructions sends
 * only the instructions — but at least one must be present, because an empty
 * save would still cost a PUT to the platform and an audit row.
 *
 * `expectedSirmaUpdatedAt` is the `updated` value the editor was looking at. The
 * server re-reads the agent immediately before writing and refuses if it has
 * moved (G4): `/client/` exposes no ETag, so this timestamp is the only version
 * token available. `null` means the agent had never been updated when we read
 * it, which is a state worth being able to express rather than conflating with
 * "did not check".
 */
export const agentUpdateSchema = z
  .object({
    presetName: z.string().trim().min(1).max(AGENT_PRESET_NAME_MAX).optional(),
    name: z.string().trim().min(1).max(AGENT_NAME_MAX).optional(),
    instructions: z.string().max(AGENT_INSTRUCTIONS_MAX).optional(),
    expectedSirmaUpdatedAt: z.string().datetime().nullable(),
  })
  .refine((value) => AGENT_FIELDS.some((field) => value[field] !== undefined), {
    message: 'Nothing to change: send at least one of presetName, name or instructions.',
  });
export type AgentUpdate = z.infer<typeof agentUpdateSchema>;

/**
 * What the operator is told when a save loses a race (G4).
 *
 * Named rather than generic, because "someone else changed this" is unactionable
 * and "mihail.genovski@sirma.com changed this at 14:32" tells them who to ask.
 */
export const agentConflictSchema = z.object({
  message: z.string(),
  expectedSirmaUpdatedAt: z.string().datetime().nullable(),
  actualSirmaUpdatedAt: z.string().datetime().nullable(),
  actualSirmaUpdatedBy: z.string().nullable(),
});
export type AgentConflict = z.infer<typeof agentConflictSchema>;

/** What happened to an agent, for the who/what/when S10 asks for. */
export const agentAuditEntrySchema = z.object({
  id: z.string().uuid(),
  /** Declared in `enums.js` beside the other domain enums, and mirrored in Prisma. */
  action: AgentAuditAction.schema,
  /** `{ field: { from, to } }`, or null for actions that changed nothing. */
  fieldDiff: z.record(z.object({ from: z.string(), to: z.string() })).nullable(),
  /** Who made the change in *our* console, where we know. */
  userEmail: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type AgentAuditEntry = z.infer<typeof agentAuditEntrySchema>;

export const agentAuditListSchema = z.object({ items: z.array(agentAuditEntrySchema) });
export type AgentAuditList = z.infer<typeof agentAuditListSchema>;
